import React from 'react'
import { Dropdown, Button, ButtonGroup, Badge } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import {
  COUNTRY_TO_LANGUAGE,
  LANGUAGE_LABELS,
  LANGUAGE_SHORT,
  PILOT_LANGUAGES,
} from '../i18n/pilotLanguages'
import { LANG_STORAGE_KEY } from '../i18n/i18n'

/**
 * LanguageToggle - role-aware language switcher placed in the Navbar.
 *
 *   admin            → not rendered (English-only)
 *   country_manager  → 2-state segmented button: [EN | <country lang>]
 *   viewer           → dropdown with all 6 languages
 *
 * Persists choice to localStorage so it survives refreshes and is picked up
 * by i18next's LanguageDetector on next page load.
 */
function LanguageToggle() {
  const { i18n, t } = useTranslation()
  const { user, isAdmin, isCountryManager, isViewer } = useAuth()

  if (!user || isAdmin) {
    return null
  }

  const currentLang = i18n.resolvedLanguage || i18n.language || 'en'

  const change = (lang) => {
    i18n.changeLanguage(lang)
    try {
      localStorage.setItem(LANG_STORAGE_KEY, lang)
    } catch {
      // localStorage may be unavailable in some embedded contexts; ignore.
    }
  }

  const betaStyle = {
    fontSize: '0.55rem',
    fontWeight: 600,
    padding: '1px 4px',
    marginLeft: '4px',
    verticalAlign: 'middle',
    backgroundColor: 'rgba(255,255,255,0.18)',
    color: 'rgba(255,255,255,0.85)',
    border: '1px solid rgba(255,255,255,0.25)',
    borderRadius: '3px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  }

  // ---------------- COUNTRY MANAGER: 2-state toggle ----------------
  if (isCountryManager) {
    const pilotLang = COUNTRY_TO_LANGUAGE[user.country_code]
    // If user's country isn't in the pilot list, fall back to admin-style (no toggle).
    if (!pilotLang) return null

    const baseBtnStyle = {
      fontSize: '0.72rem',
      fontWeight: 600,
      padding: '3px 10px',
      lineHeight: 1.2,
      borderColor: 'rgba(255,255,255,0.4)',
    }
    const activeStyle = {
      ...baseBtnStyle,
      backgroundColor: 'rgba(255,255,255,0.9)',
      color: 'var(--color-primary-dark)',
    }
    const inactiveStyle = {
      ...baseBtnStyle,
      backgroundColor: 'transparent',
      color: 'rgba(255,255,255,0.85)',
    }

    return (
      <div className="d-flex align-items-center ms-3" title={t('language_toggle.title')}>
        <ButtonGroup size="sm" aria-label={t('language_toggle.label')}>
          <Button
            variant="outline-light"
            style={currentLang === 'en' ? activeStyle : inactiveStyle}
            onClick={() => change('en')}
          >
            {LANGUAGE_SHORT.en}
          </Button>
          <Button
            variant="outline-light"
            style={currentLang === pilotLang ? activeStyle : inactiveStyle}
            onClick={() => change(pilotLang)}
          >
            {LANGUAGE_SHORT[pilotLang]}
          </Button>
        </ButtonGroup>
        <span style={betaStyle}>{t('common.beta')}</span>
      </div>
    )
  }

  // ---------------- VIEWER: dropdown with all 6 ----------------
  if (isViewer) {
    return (
      <div className="d-flex align-items-center ms-3" title={t('language_toggle.title')}>
        <Dropdown align="end">
          <Dropdown.Toggle
            variant="outline-light"
            size="sm"
            style={{
              fontSize: '0.72rem',
              fontWeight: 600,
              padding: '3px 10px',
              lineHeight: 1.2,
              borderColor: 'rgba(255,255,255,0.4)',
              color: 'rgba(255,255,255,0.9)',
            }}
          >
            {LANGUAGE_SHORT[currentLang] || 'EN'}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {PILOT_LANGUAGES.map((lng) => (
              <Dropdown.Item
                key={lng}
                active={lng === currentLang}
                onClick={() => change(lng)}
              >
                <span style={{ display: 'inline-block', minWidth: '2rem', fontWeight: 600, color: '#888' }}>
                  {LANGUAGE_SHORT[lng]}
                </span>
                {LANGUAGE_LABELS[lng]}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        <span style={betaStyle}>{t('common.beta')}</span>
      </div>
    )
  }

  return null
}

export default LanguageToggle
